import { z } from "zod";

export const smbWeeklyGroupSchema = z.object({
  personaId: z.string().max(160), productId: z.string().max(160), angleId: z.string().max(160),
  templates: z.array(z.object({
    templateId: z.string().min(1).max(160),
    quantity: z.number().int().min(1).max(5),
    ratios: z.array(z.enum(["1:1", "4:5", "9:16"])).min(1).max(3)
      .refine(v => new Set(v).size === v.length, "Ratios must be unique"),
  }).strict()).max(10).refine(v => new Set(v.map(t => t.templateId)).size === v.length, "Templates must be unique"),
}).strict();
export const smbWeeklyDraftSchema = z.object({
  revision: z.number().int().nonnegative(),
  groups: z.array(smbWeeklyGroupSchema).max(10),
}).strict().refine(v => weeklyImageCount(v.groups) <= 30, "A batch supports at most 30 images");
export type SmbWeeklyGroup = z.infer<typeof smbWeeklyGroupSchema>;
export function weeklyImageCount(groups: SmbWeeklyGroup[]) {
  return groups.reduce((n, g) => n + g.templates.reduce((s, t) => s + t.quantity * t.ratios.length, 0), 0);
}
/** PostgreSQL JSONB reorders object keys. Compare selections, not serialization order. */
export function weeklySelectionKey(groups: SmbWeeklyGroup[]) {
  return JSON.stringify(groups.map(g => [g.personaId, g.productId, g.angleId,
    g.templates.map(t => [t.templateId, t.quantity, t.ratios])]));
}
/** Monday 00:00 UTC, identified by its YYYY-MM-DD start date. No scheduler. */
export function smbWeekStart(now = new Date()) {
  const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  day.setUTCDate(day.getUTCDate() - (day.getUTCDay() + 6) % 7);
  return day.toISOString().slice(0, 10);
}
export type SmbWeeklyCatalog = {
  personas: { id: string; name: string }[];
  products: { id: string; name: string }[];
  angles: { id: string; name: string; personaId: string | null; productId: string | null }[];
  templates: { id: string; name: string; thumbnail: string | null; suggested: boolean }[];
};
export type SmbWeeklyBatch = {
  id: string; week: string; revision: number; groups: SmbWeeklyGroup[];
  submitted: boolean; status: string; count: number; hold: string | null;
  labels?: SmbWeeklyCatalog;
  targets: { id: string; state: string; generationId: string | null;
    assets: { url: string; name: string | null }[] }[];
};
export type SmbWeeklyView = {
  week: string; convention: string; blockers: string[]; catalog: SmbWeeklyCatalog;
  brandId: string | null; costUsd: number | null; costCapUsd: string | null;
  current: SmbWeeklyBatch | null; history: SmbWeeklyBatch[];
};
